if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.variantIdInput = this.form.querySelector('[name="id"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.submitButtonText = this.submitButton
          ? this.submitButton.querySelector('[data-add-to-cart-label], span:not(.sold-out-message):not([data-in-cart-controls])')
          : null;
        this.submitButtonSoldOutMessage = this.submitButton ? this.submitButton.querySelector('.sold-out-message') : null;
        this.inCartControls = this.submitButton ? this.submitButton.querySelector('[data-in-cart-controls]') : null;
        this.inCartCountInput = this.submitButton ? this.submitButton.querySelector('[data-cart-count-input]') : null;
        this.inCartRemoveButton = this.querySelector('[data-remove-from-cart]');

        this.defaultButtonStyle = this.submitButton?.dataset.defaultButtonStyle || 'button--primary';
        this.currentCartState = null;
        this.currentVariantLineItem = null;
        this.currentVariantQuantity = 0;
        this.pendingVariantQuantities = {};
        this.isUpdatingQuantity = false;
        this.quantityTypingTimeout = null;

        this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onQuantityInput = this.onQuantityInput.bind(this);
        this.onQuantityInputBlur = this.onQuantityInputBlur.bind(this);
        this.onQuantityInputKeydown = this.onQuantityInputKeydown.bind(this);
        this.onQuantityInputClick = this.onQuantityInputClick.bind(this);
        this.onRemoveFromCartClick = this.onRemoveFromCartClick.bind(this);
      }

      connectedCallback() {
        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmitButtonClick);
        }
        if (this.inCartCountInput) {
          this.inCartCountInput.addEventListener('input', this.onQuantityInput);
          this.inCartCountInput.addEventListener('blur', this.onQuantityInputBlur);
          this.inCartCountInput.addEventListener('keydown', this.onQuantityInputKeydown);
          this.inCartCountInput.addEventListener('click', this.onQuantityInputClick);
        }
        if (this.inCartRemoveButton) {
          this.inCartRemoveButton.addEventListener('click', this.onRemoveFromCartClick);
        }

        if (typeof subscribe === 'function') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (this.isFullCartState(event?.cartData)) {
              this.currentCartState = event.cartData;
              this.updateInCartButtonState();
              return;
            }

            this.refreshCartState();
          });
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const eventSectionId = event?.data?.sectionId || event?.sectionId;
            if (!eventSectionId || eventSectionId === this.dataset.sectionId) {
              this.onVariantChange(event);
            }
          });
        }

        if (this.variantIdInput) {
          this.variantIdInput.addEventListener('change', this.onVariantChange);
        }

        this.refreshCartState();
      }

      disconnectedCallback() {
        if (this.submitButton) {
          this.submitButton.removeEventListener('click', this.onSubmitButtonClick);
        }
        if (this.inCartCountInput) {
          this.inCartCountInput.removeEventListener('input', this.onQuantityInput);
          this.inCartCountInput.removeEventListener('blur', this.onQuantityInputBlur);
          this.inCartCountInput.removeEventListener('keydown', this.onQuantityInputKeydown);
          this.inCartCountInput.removeEventListener('click', this.onQuantityInputClick);
        }
        if (this.inCartRemoveButton) {
          this.inCartRemoveButton.removeEventListener('click', this.onRemoveFromCartClick);
        }

        if (this.variantIdInput) {
          this.variantIdInput.removeEventListener('change', this.onVariantChange);
        }

        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      onVariantChange(event) {
        const sourceButton = event?.data?.html?.getElementById?.(this.submitButton?.id);
        if (sourceButton && this.submitButton) {
          [
            'marketCountry',
            'variantId',
            'shopifyInventoryQuantity',
            'canadaAvailable',
            'canadaBuffer',
            'marketInventoryCap',
            'quantityMin',
            'quantityIncrement',
          ].forEach((attribute) => {
            const value = sourceButton.dataset[attribute];
            if (value === undefined) {
              delete this.submitButton.dataset[attribute];
            } else {
              this.submitButton.dataset[attribute] = value;
            }
          });
        }
        this.updateInCartButtonState();
      }

      onSubmitButtonClick(event) {
        if (!this.isInCartState()) return;

        event.preventDefault();
        event.stopPropagation();

        const decrementTarget = event.target.closest('[data-quantity-decrement]');
        const incrementTarget = event.target.closest('[data-quantity-increment]');

        if (!decrementTarget && !incrementTarget) return;

        const quantityMin = this.getQuantityMinimum();
        const quantityIncrement = this.getQuantityIncrement();
        const requestedQuantity = incrementTarget
          ? (this.currentVariantQuantity > 0 ? this.currentVariantQuantity + quantityIncrement : quantityMin)
          : (this.currentVariantQuantity <= quantityMin ? 0 : this.currentVariantQuantity - quantityIncrement);
        const nextQuantity = this.clampToMarketInventory(requestedQuantity, true);
        if (nextQuantity === this.currentVariantQuantity) return;
        this.changeCartQuantity(nextQuantity);
      }

      onRemoveFromCartClick(event) {
        if (!this.isInCartState()) return;
        event.preventDefault();
        event.stopPropagation();
        this.changeCartQuantity(0);
      }

      onQuantityInput() {
        if (!this.isInCartState() || !this.inCartCountInput) return;
        if (this.quantityTypingTimeout) clearTimeout(this.quantityTypingTimeout);

        this.quantityTypingTimeout = setTimeout(() => {
          this.commitTypedQuantity();
        }, 700);
      }

      onQuantityInputBlur() {
        this.commitTypedQuantity();
      }

      onQuantityInputKeydown(event) {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.commitTypedQuantity();
      }

      onQuantityInputClick(event) {
        event.stopPropagation();
      }

      commitTypedQuantity() {
        if (!this.isInCartState() || !this.inCartCountInput) return;
        if (this.quantityTypingTimeout) {
          clearTimeout(this.quantityTypingTimeout);
          this.quantityTypingTimeout = null;
        }

        const parsedQuantity = Number.parseInt(this.inCartCountInput.value, 10);
        const requestedQuantity = Number.isFinite(parsedQuantity) ? Math.max(parsedQuantity, 0) : this.currentVariantQuantity;
        const normalizedQuantity = this.normalizeQuantityRule(requestedQuantity);
        const nextQuantity = this.clampToMarketInventory(normalizedQuantity, true);

        if (nextQuantity === this.currentVariantQuantity) {
          this.inCartCountInput.value = `${this.currentVariantQuantity}`;
          return;
        }

        this.changeCartQuantity(nextQuantity);
      }

      async refreshCartState() {
        try {
          const response = await fetch(`${routes.cart_url}.js`);
          if (!response.ok) return;
          const cartState = await response.json();
          this.currentCartState = cartState;
          this.updateInCartButtonState();
        } catch (error) {
          console.error(error);
        }
      }

      getCurrentVariantId() {
        if (!this.variantIdInput) return null;
        const variantId = Number(this.variantIdInput.value);
        return Number.isFinite(variantId) ? variantId : null;
      }

      getPendingVariantQuantity(variantId) {
        if (!variantId) return 0;
        const pendingQuantity = Number(this.pendingVariantQuantities[variantId]);
        return Number.isFinite(pendingQuantity) ? Math.max(pendingQuantity, 0) : 0;
      }

      getCurrentVariantLineItem() {
        return this.getCurrentVariantLineItems()[0] || null;
      }

      getCurrentVariantLineItems() {
        if (!this.currentCartState?.items) return [];
        const variantId = this.getCurrentVariantId();
        if (!variantId) return [];

        return this.currentCartState.items.filter(
          (item) => Number(item.variant_id) === variantId || Number(item.id) === variantId
        );
      }

      getMarketInventoryCap() {
        if (!this.submitButton || this.submitButton.dataset.marketInventoryCap === undefined) return null;
        const cap = Number.parseInt(this.submitButton.dataset.marketInventoryCap, 10);
        return Number.isFinite(cap) ? Math.max(cap, 0) : null;
      }

      getQuantityMinimum() {
        const minimum = Number.parseInt(this.submitButton?.dataset.quantityMin, 10);
        return Number.isFinite(minimum) ? Math.max(minimum, 1) : 1;
      }

      getQuantityIncrement() {
        const increment = Number.parseInt(this.submitButton?.dataset.quantityIncrement, 10);
        return Number.isFinite(increment) ? Math.max(increment, 1) : 1;
      }

      normalizeQuantityRule(quantity) {
        if (!Number.isFinite(quantity) || quantity <= 0) return 0;
        const minimum = this.getQuantityMinimum();
        const increment = this.getQuantityIncrement();
        if (quantity <= minimum) return minimum;
        return quantity - ((quantity - minimum) % increment);
      }

      getVariantLineQuantityUpdates(targetQuantity) {
        const lines = this.getCurrentVariantLineItems().filter((item) => item.key);
        if (lines.length < 2) return null;

        const minimum = this.getQuantityMinimum();
        const increment = this.getQuantityIncrement();
        let activeLineCount = 0;
        for (let count = lines.length; count >= 1; count -= 1) {
          if (targetQuantity >= count * minimum && (targetQuantity - count * minimum) % increment === 0) {
            activeLineCount = count;
            break;
          }
        }

        const quantities = lines.map((_, index) => (index < activeLineCount ? minimum : 0));
        let remaining = Math.max(targetQuantity - activeLineCount * minimum, 0);
        for (let index = 0; index < activeLineCount && remaining > 0; index += 1) {
          const currentQuantity = Math.max(Number(lines[index].quantity) || 0, minimum);
          const existingCapacity = Math.max(currentQuantity - minimum, 0);
          const addition = Math.min(existingCapacity, remaining) - (Math.min(existingCapacity, remaining) % increment);
          quantities[index] += addition;
          remaining -= addition;
        }
        if (remaining > 0 && activeLineCount > 0) quantities[0] += remaining;

        const updates = {};
        lines.forEach((item, index) => {
          updates[item.key] = quantities[index] || 0;
        });
        return updates;
      }

      clampToMarketInventory(quantity, showError = false) {
        const cap = this.getMarketInventoryCap();
        if (cap === null || quantity <= cap) return quantity;

        if (showError) {
          const message = (window.cartStrings?.marketInventoryError || 'Only [quantity] units are available in your market.')
            .replace('[quantity]', cap);
          this.handleErrorMessage(message);
        }
        return cap;
      }

      isInCartState() {
        return this.submitButton?.classList.contains('product-form__submit--in-cart') || false;
      }

      updateInCartButtonState() {
        if (!this.submitButton) return;

        const variantId = this.getCurrentVariantId();
        this.currentVariantLineItem = this.getCurrentVariantLineItem();
        const cartQuantity = this.getCurrentVariantLineItems().reduce(
          (total, item) => total + (Number(item.quantity) || 0),
          0
        );
        const pendingQuantity = this.getPendingVariantQuantity(variantId);
        this.currentVariantQuantity = Math.max(cartQuantity, pendingQuantity);

        if (variantId && cartQuantity > 0 && this.pendingVariantQuantities[variantId] !== undefined) {
          delete this.pendingVariantQuantities[variantId];
        }

        const marketCap = this.getMarketInventoryCap();
        const nextAddQuantity = this.currentVariantQuantity > 0 ? this.getQuantityIncrement() : this.getQuantityMinimum();
        const marketCapReached = marketCap !== null && this.currentVariantQuantity + nextAddQuantity > marketCap;
        this.submitButton.dataset.marketCapReached = `${marketCapReached}`;
        if (marketCapReached && !this.inCartControls) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.dataset.marketCapDisabled = 'true';
        } else if (this.submitButton.dataset.marketCapDisabled === 'true') {
          this.submitButton.removeAttribute('disabled');
          delete this.submitButton.dataset.marketCapDisabled;
        }

        if (!this.inCartControls || !this.inCartCountInput || !this.submitButtonText) return;

        const isUnavailable = this.submitButton.hasAttribute('disabled');
        const shouldShowInCart = this.currentVariantQuantity > 0 && !isUnavailable;

        this.submitButton.classList.remove('button--primary', 'button--secondary', 'product-form__submit--in-cart');
        if (shouldShowInCart) {
          this.submitButton.classList.add('button--secondary', 'product-form__submit--in-cart');
          this.submitButton.type = 'button';
          this.submitButtonText.classList.add('hidden');
          if (this.submitButtonSoldOutMessage) this.submitButtonSoldOutMessage.classList.add('hidden');
          this.inCartControls.classList.remove('hidden');
          if (this.inCartRemoveButton) this.inCartRemoveButton.classList.remove('hidden');
          this.inCartCountInput.value = `${this.currentVariantQuantity}`;
          this.inCartCountInput.step = `${this.getQuantityIncrement()}`;
          if (marketCap === null) {
            this.inCartCountInput.removeAttribute('max');
          } else {
            this.inCartCountInput.max = `${marketCap}`;
          }
          const incrementControl = this.submitButton.querySelector('[data-quantity-increment]');
          if (incrementControl) {
            incrementControl.classList.toggle('disabled', marketCapReached);
            incrementControl.setAttribute('aria-disabled', `${marketCapReached}`);
          }
          this.submitButton.setAttribute(
            'aria-label',
            `Quantity in cart: ${this.currentVariantQuantity}. Use minus and plus controls to adjust quantity.`
          );
          return;
        }

        this.submitButton.classList.add(this.defaultButtonStyle);
        this.submitButton.type = 'submit';
        this.inCartControls.classList.add('hidden');
        if (this.inCartRemoveButton) this.inCartRemoveButton.classList.add('hidden');
        this.inCartCountInput.value = '0';
        this.submitButtonText.classList.remove('hidden');
        this.restoreSubmitText();
        this.submitButton.removeAttribute('aria-label');
      }

      async changeCartQuantity(quantity) {
        if (this.isUpdatingQuantity) return;

        const variantId = this.getCurrentVariantId();
        if (!variantId) return;

        quantity = this.clampToMarketInventory(this.normalizeQuantityRule(quantity), true);
        const previousQuantity = this.currentVariantQuantity;
        const shouldCleanupAddonDependencies = quantity < previousQuantity;
        const changedProductId = Number(this.currentVariantLineItem?.product_id);

        this.isUpdatingQuantity = true;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          const requestBody = { ...this.getSectionsPayload() };
          let endpoint = routes.cart_change_url;
          const variantLineUpdates = quantity < previousQuantity
            ? this.getVariantLineQuantityUpdates(quantity)
            : null;

          if (variantLineUpdates) {
            endpoint = routes.cart_update_url;
            requestBody.updates = variantLineUpdates;
          } else if (this.currentVariantLineItem?.key) {
            requestBody.id = this.currentVariantLineItem.key;
            const currentLineQuantity = Number(this.currentVariantLineItem.quantity) || 0;
            requestBody.quantity = Math.max(currentLineQuantity + quantity - previousQuantity, 0);
          } else {
            endpoint = routes.cart_add_url;
            requestBody.id = variantId;
            requestBody.quantity = quantity;
          }

          const response = await fetch(endpoint, {
            ...fetchConfig('javascript'),
            body: JSON.stringify(requestBody),
          });
          const parsedResponse = await response.json();

          if (parsedResponse.status) {
            this.handleErrorMessage(parsedResponse.description || parsedResponse.message);
            return;
          }

          if (this.isFullCartState(parsedResponse)) {
            this.currentCartState = parsedResponse;
            this.updateInCartButtonState();
          } else {
            await this.refreshCartState();
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: parsedResponse,
            shouldCleanupAddonDependencies,
            changedProductId: Number.isFinite(changedProductId) ? changedProductId : null,
          });

          if (this.cart) {
            this.renderCartContents(parsedResponse);
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.isUpdatingQuantity = false;
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      isFullCartState(cartState) {
        return (
          Array.isArray(cartState?.items) &&
          Number.isFinite(Number(cartState.item_count)) &&
          Number.isFinite(Number(cartState.total_price))
        );
      }

      getSectionsPayload() {
        if (!this.cart) return {};

        return {
          sections: this.cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (!this.currentCartState) await this.refreshCartState();

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        this.setSubmittingText();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const hasFormQuantity = formData.has('quantity');
        const requestedFormQuantity = Number.parseInt(formData.get('quantity'), 10);
        let requestedAddQuantity = Number.isFinite(requestedFormQuantity) && requestedFormQuantity > 0
          ? requestedFormQuantity
          : (this.currentVariantQuantity > 0 ? this.getQuantityIncrement() : this.getQuantityMinimum());
        if (!hasFormQuantity) {
          requestedAddQuantity = this.currentVariantQuantity > 0
            ? this.getQuantityIncrement()
            : this.getQuantityMinimum();
        }
        const marketCap = this.getMarketInventoryCap();
        if (marketCap !== null) {
          const remainingQuantity = Math.max(marketCap - this.currentVariantQuantity, 0);
          const allowedAddQuantity = this.normalizeQuantityRule(remainingQuantity);
          if (remainingQuantity <= 0 || allowedAddQuantity > remainingQuantity) {
            const message = (window.cartStrings?.marketInventoryError || 'Only [quantity] units are available in your market.')
              .replace('[quantity]', marketCap);
            this.handleErrorMessage(message);
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            this.restoreSubmitText();
            return;
          }
          formData.set('quantity', Math.min(requestedAddQuantity, allowedAddQuantity));
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (this.isFullCartState(response)) {
              this.currentCartState = response;
            }
            const addedVariantId = Number(formData.get('id'));
            if (addedVariantId && !Array.isArray(response.items)) {
              const formQuantity = Number.parseInt(formData.get('quantity'), 10);
              const addQuantity = Number.isFinite(formQuantity) && formQuantity > 0 ? formQuantity : 1;
              this.pendingVariantQuantities[addedVariantId] = Math.max(this.currentVariantQuantity + addQuantity, addQuantity);
            }
            this.updateInCartButtonState();

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.renderCartContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.renderCartContents(response);
            }
            this.triggerAddToCartFeedback();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            if (!this.error) this.restoreSubmitText();
          });
      }

      renderCartContents(response) {
        if (this.cart && this.cart.matches('cart-drawer')) {
          this.cart.renderContents(response, { open: false });
          return;
        }

        this.cart.renderContents(response);
      }

      triggerAddToCartFeedback() {
        this.revealStickyHeader();
        this.wobbleCartIcon();
      }

      revealStickyHeader() {
        const stickyHeader = document.querySelector('.shopify-section-header-sticky');
        if (stickyHeader && stickyHeader.classList.contains('shopify-section-header-hidden')) {
          stickyHeader.classList.remove('shopify-section-header-hidden');
        }
      }

      wobbleCartIcon() {
        const cartIcon = document.querySelector('.header__icon--cart');
        if (!cartIcon) return;
        cartIcon.classList.remove('is-wobbling');
        void cartIcon.offsetWidth;
        cartIcon.classList.add('is-wobbling');
        cartIcon.addEventListener(
          'animationend',
          () => {
            cartIcon.classList.remove('is-wobbling');
          },
          { once: true }
        );
      }

      setSubmittingText() {
        if (!this.submitButtonText) return;
        if (!this.submitButtonText.dataset.originalText) {
          this.submitButtonText.dataset.originalText = this.submitButtonText.textContent;
        }
        this.submitButtonText.classList.remove('hidden');
        if (this.submitButtonSoldOutMessage) {
          this.submitButtonSoldOutMessage.classList.add('hidden');
        }
        this.submitButtonText.textContent = 'adding...';
      }

      restoreSubmitText() {
        if (!this.submitButtonText || this.isInCartState()) return;
        const originalText = this.submitButtonText.dataset.originalText;
        if (originalText && !this.submitButtonText.classList.contains('hidden')) {
          this.submitButtonText.textContent = originalText;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
